import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useJournal } from './JournalContext';
import { useAuth } from './AuthContext';
import { useCrisis } from './CrisisContext';
import { supabase } from '../lib/supabase';
import { analyzeJournalEntry } from '../lib/openai';

export interface SentimentAnalysis {
  score: number; // -1 to 1
  magnitude: number; // 0 to 1
  emotions: {
    joy: number;
    sadness: number;
    anger: number;
    fear: number;
    surprise: number;
    disgust: number;
  };
  confidence: number;
}

export interface CognitiveDistortion {
  type: string;
  description: string;
  severity: 'low' | 'medium' | 'high';
  examples: string[];
  suggestion: string;
}

export interface BehavioralTrigger {
  trigger: string;
  frequency: number;
  associatedMoods: number[];
  patterns: string[];
  recommendations: string[];
}

export interface RiskAssessment {
  level: 'low' | 'medium' | 'high' | 'crisis';
  factors: string[];
  recommendations: string[];
  resources: string[];
  requiresIntervention: boolean;
}

export interface TherapeuticResponse {
  type: 'validation' | 'cbt' | 'mindfulness' | 'coping' | 'goal-setting';
  message: string;
  techniques: string[];
  exercises: string[];
  followUp: string[];
}

export interface AIInsight {
  id: string;
  type: 'pattern' | 'progress' | 'concern' | 'celebration';
  title: string;
  description: string;
  actionItems: string[];
  priority: 'low' | 'medium' | 'high';
  createdAt: Date;
}

export interface ProgressSummary {
  period: 'week' | 'month';
  moodTrend: 'improving' | 'stable' | 'declining';
  keyInsights: string[];
  achievements: string[];
  areasForFocus: string[];
  goalProgress: { goal: string; progress: number }[];
  recommendations: string[];
}

interface AIAnalysisContextType {
  analyzeSentiment: (text: string) => Promise<SentimentAnalysis>;
  detectCognitiveDistortions: (text: string) => Promise<CognitiveDistortion[]>;
  identifyTriggers: () => BehavioralTrigger[];
  assessRisk: (entryId: string) => Promise<RiskAssessment>;
  generateTherapeuticResponse: (entryId: string) => Promise<TherapeuticResponse>;
  getInsights: () => AIInsight[];
  generateProgressSummary: (period: 'week' | 'month') => Promise<ProgressSummary>;
  getMoodPatterns: () => any[];
  getBehavioralPatterns: () => any;
  getGoalProgress: () => any[];
  isAnalyzing: boolean;
  setIsAnalyzing: (isAnalyzing: boolean) => void;
  lastAnalysis: Date | null;
}

const AIAnalysisContext = createContext<AIAnalysisContextType | undefined>(undefined);

export const useAIAnalysis = () => {
  const context = useContext(AIAnalysisContext);
  if (context === undefined) {
    throw new Error('useAIAnalysis must be used within an AIAnalysisProvider');
  }
  return context;
};

export const AIAnalysisProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { entries } = useJournal();
  const { user } = useAuth();
  const { detectCrisis } = useCrisis();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [lastAnalysis, setLastAnalysis] = useState<Date | null>(null);
  const [insights, setInsights] = useState<AIInsight[]>([]);
  const [analysisCache, setAnalysisCache] = useState<Record<string, any>>({});

  // Analyze sentiment using OpenAI
  const analyzeSentiment = async (text: string): Promise<SentimentAnalysis> => {
    setIsAnalyzing(true);
    
    try {
      console.log("Analyzing sentiment for text:", text.substring(0, 50) + "...");
      
      // Check if we have a cached analysis for this text
      const cacheKey = `sentiment_${text.substring(0, 100)}`;
      if (analysisCache[cacheKey]) {
        console.log("Using cached sentiment analysis");
        return analysisCache[cacheKey];
      }
      
      // Try to use OpenAI for comprehensive analysis
      try {
        const analysis = await analyzeJournalEntry({
          content: text,
          mood: 5, // Default values since we don't have the full entry
          anxiety: 5,
          energy: 5,
          sleepQuality: 5,
          tags: [],
          triggers: []
        });
        
        const result: SentimentAnalysis = {
          score: analysis.sentiment.score,
          magnitude: 0.8, // Estimated from sentiment strength
          emotions: analysis.sentiment.emotions,
          confidence: 0.9 // OpenAI is generally confident
        };
        
        // Cache the result
        setAnalysisCache(prev => ({
          ...prev,
          [cacheKey]: result
        }));
        
        return result;
      } catch (error) {
        console.error("OpenAI sentiment analysis failed, falling back to local analysis:", error);
        // Fall back to simulated analysis
        const result = await simulateSentimentAnalysis(text);
        
        // Cache the result
        setAnalysisCache(prev => ({
          ...prev,
          [cacheKey]: result
        }));
        
        return result;
      }
    } catch (error) {
      console.error("Error in sentiment analysis:", error);
      throw error;
    } finally {
      setIsAnalyzing(false);
      setLastAnalysis(new Date());
    }
  };

  // Simulate sentiment analysis
  const simulateSentimentAnalysis = async (text: string): Promise<SentimentAnalysis> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Simple sentiment analysis simulation
    const positiveWords = ['happy', 'good', 'great', 'amazing', 'wonderful', 'love', 'joy', 'excited', 'grateful', 'peaceful'];
    const negativeWords = ['sad', 'bad', 'terrible', 'awful', 'hate', 'angry', 'depressed', 'anxious', 'worried', 'stressed'];
    
    const words = text.toLowerCase().split(/\s+/);
    let positiveCount = 0;
    let negativeCount = 0;
    
    words.forEach(word => {
      if (positiveWords.some(pw => word.includes(pw))) positiveCount++;
      if (negativeWords.some(nw => word.includes(nw))) negativeCount++;
    });
    
    const totalSentimentWords = positiveCount + negativeCount;
    const score = totalSentimentWords > 0 ? (positiveCount - negativeCount) / totalSentimentWords : 0;
    
    const result = {
      score: Math.max(-1, Math.min(1, score)),
      magnitude: Math.min(1, totalSentimentWords / words.length * 10),
      emotions: {
        joy: Math.random() * 0.3 + (score > 0 ? 0.4 : 0),
        sadness: Math.random() * 0.3 + (score < 0 ? 0.4 : 0),
        anger: Math.random() * 0.2,
        fear: Math.random() * 0.3,
        surprise: Math.random() * 0.2,
        disgust: Math.random() * 0.1
      },
      confidence: 0.75 + Math.random() * 0.2
    };
    
    console.log("Sentiment analysis result:", result);
    return result;
  };

  const detectCognitiveDistortions = async (text: string): Promise<CognitiveDistortion[]> => {
    try {
      console.log("Detecting cognitive distortions in text:", text.substring(0, 50) + "...");
      
      // Check if we have a cached analysis for this text
      const cacheKey = `distortions_${text.substring(0, 100)}`;
      if (analysisCache[cacheKey]) {
        console.log("Using cached cognitive distortions");
        return analysisCache[cacheKey];
      }
      
      // Try to use OpenAI for comprehensive analysis
      try {
        const analysis = await analyzeJournalEntry({
          content: text,
          mood: 5, // Default values since we don't have the full entry
          anxiety: 5,
          energy: 5,
          sleepQuality: 5,
          tags: [],
          triggers: []
        });
        
        // Cache the result
        setAnalysisCache(prev => ({
          ...prev,
          [cacheKey]: analysis.cognitiveDistortions
        }));
        
        return analysis.cognitiveDistortions;
      } catch (error) {
        console.error("OpenAI cognitive distortion analysis failed, falling back to local analysis:", error);
        
        // Simulate API delay
        await new Promise(resolve => setTimeout(resolve, 800));
        
        const distortions: CognitiveDistortion[] = [];
        const lowerText = text.toLowerCase();
        
        // Cognitive distortion patterns
        const distortionPatterns = {
          'all-or-nothing': {
            keywords: ['always', 'never', 'completely', 'totally', 'absolutely', 'everything', 'nothing'],
            description: 'Seeing things in black and white categories',
            suggestion: 'Try to find the middle ground and consider shades of gray in situations.'
          },
          'overgeneralization': {
            keywords: ['everyone', 'no one', 'all the time', 'every time'],
            description: 'Drawing broad conclusions from single events',
            suggestion: 'Look for specific evidence and avoid generalizing from one experience.'
          },
          'mental-filter': {
            keywords: ['only', 'just', 'but', 'however', 'except'],
            description: 'Focusing on negative details while ignoring positives',
            suggestion: 'Practice acknowledging both positive and negative aspects of situations.'
          },
          'catastrophizing': {
            keywords: ['disaster', 'terrible', 'awful', 'worst', 'ruined', 'destroyed'],
            description: 'Expecting the worst possible outcome',
            suggestion: 'Consider more realistic outcomes and challenge worst-case thinking.'
          },
          'mind-reading': {
            keywords: ['they think', 'he thinks', 'she thinks', 'probably thinks'],
            description: 'Assuming you know what others are thinking',
            suggestion: 'Focus on facts rather than assumptions about others\' thoughts.'
          },
          'should-statements': {
            keywords: ['should', 'must', 'ought to', 'have to', 'need to'],
            description: 'Using rigid rules about how things should be',
            suggestion: 'Replace "should" with "could" or "would prefer" for more flexibility.'
          }
        };
        
        Object.entries(distortionPatterns).forEach(([type, pattern]) => {
          const matches = pattern.keywords.filter(keyword => lowerText.includes(keyword));
          
          if (matches.length > 0) {
            distortions.push({
              type,
              description: pattern.description,
              severity: matches.length > 2 ? 'high' : matches.length > 1 ? 'medium' : 'low',
              examples: matches,
              suggestion: pattern.suggestion
            });
          }
        });
        
        // Cache the result
        setAnalysisCache(prev => ({
          ...prev,
          [cacheKey]: distortions
        }));
        
        console.log("Detected cognitive distortions:", distortions);
        return distortions;
      }
    } catch (error) {
      console.error("Error detecting cognitive distortions:", error);
      throw error;
    }
  };

  const identifyTriggers = (): BehavioralTrigger[] => {
    try {
      console.log("Identifying behavioral triggers from entries");
      
      const triggerMap: Record<string, { count: number; moods: number[] }> = {};
      
      entries.forEach(entry => {
        entry.triggers.forEach(trigger => {
          if (!triggerMap[trigger]) {
            triggerMap[trigger] = { count: 0, moods: [] };
          }
          triggerMap[trigger].count++;
          triggerMap[trigger].moods.push(entry.mood);
        });
      });
      
      const triggers = Object.entries(triggerMap).map(([trigger, data]) => ({
        trigger,
        frequency: data.count,
        associatedMoods: data.moods,
        patterns: [`Occurs ${data.count} times`, `Average mood impact: ${(data.moods.reduce((a, b) => a + b, 0) / data.moods.length).toFixed(1)}`],
        recommendations: [
          'Practice mindfulness when this trigger appears',
          'Develop coping strategies specific to this situation',
          'Consider discussing this pattern with a therapist'
        ]
      }));
      
      console.log("Identified triggers:", triggers);
      return triggers;
    } catch (error) {
      console.error("Error identifying triggers:", error);
      return [];
    }
  };

  const assessRisk = async (entryId: string): Promise<RiskAssessment> => {
    try {
      console.log("Assessing risk for entry:", entryId);
      
      const entry = entries.find(e => e.id === entryId);
      if (!entry) {
        console.log("Entry not found for risk assessment");
        return {
          level: 'low',
          factors: [],
          recommendations: [],
          resources: [],
          requiresIntervention: false
        };
      }
      
      // Check if we have a cached analysis for this entry
      const cacheKey = `risk_${entryId}`;
      if (analysisCache[cacheKey]) {
        console.log("Using cached risk assessment");
        return analysisCache[cacheKey];
      }
      
      // Try to use OpenAI for comprehensive analysis
      try {
        const analysis = await analyzeJournalEntry({
          content: entry.content,
          mood: entry.mood,
          anxiety: entry.anxiety,
          energy: entry.energy,
          sleepQuality: entry.sleepQuality,
          tags: entry.tags,
          triggers: entry.triggers
        });
        
        const result = {
          ...analysis.riskAssessment,
          requiresIntervention: analysis.riskAssessment.level === 'crisis' || analysis.riskAssessment.level === 'high'
        };
        
        // Cache the result
        setAnalysisCache(prev => ({
          ...prev,
          [cacheKey]: result
        }));
        
        return result;
      } catch (error) {
        console.error("OpenAI risk assessment failed, falling back to local assessment:", error);
        
        // Run crisis detection
        const crisisDetection = await detectCrisis(entry.content + ' ' + entry.title, 'text');
        
        let level: 'low' | 'medium' | 'high' | 'crisis' = 'low';
        let factors: string[] = [];
        let requiresIntervention = false;
        
        if (crisisDetection) {
          level = crisisDetection.severity === 'critical' ? 'crisis' : crisisDetection.severity;
          factors = crisisDetection.triggers;
          requiresIntervention = crisisDetection.interventionRequired;
        }
        
        if (entry.mood <= 3) factors.push('Very low mood');
        if (entry.anxiety >= 8) factors.push('High anxiety levels');
        if (entry.energy <= 3) factors.push('Very low energy levels');
        if (entry.sleepQuality <= 3) factors.push('Poor sleep quality');
        
        const resources = level === 'crisis' ? [
          'Lifeline Barbados: 1(246)536-4500',
          'Crisis Text Line: Text HOME to 741741',
          'Emergency Services: 911'
        ] : [
          'Consider reaching out to a trusted friend or family member',
          'Contact your mental health provider',
          'Practice grounding techniques'
        ];
        
        const result = {
          level,
          factors,
          recommendations: [
            'Monitor mood patterns closely',
            'Engage in self-care activities',
            'Consider professional support'
          ],
          resources,
          requiresIntervention
        };
        
        // Cache the result
        setAnalysisCache(prev => ({
          ...prev,
          [cacheKey]: result
        }));
        
        console.log("Risk assessment result:", result);
        return result;
      }
    } catch (error) {
      console.error("Error in risk assessment:", error);
      throw error;
    }
  };

  const generateTherapeuticResponse = async (entryId: string): Promise<TherapeuticResponse> => {
    setIsAnalyzing(true);
    
    try {
      console.log("Generating therapeutic response for entry:", entryId);
      
      const entry = entries.find(e => e.id === entryId);
      if (!entry) {
        throw new Error('Entry not found');
      }
      
      // Check if we have a cached analysis for this entry
      const cacheKey = `therapeutic_${entryId}`;
      if (analysisCache[cacheKey]) {
        console.log("Using cached therapeutic response");
        return analysisCache[cacheKey];
      }
      
      // Use OpenAI to analyze the journal entry
      const analysis = await analyzeJournalEntry({
        content: entry.content,
        mood: entry.mood,
        anxiety: entry.anxiety,
        energy: entry.energy,
        sleepQuality: entry.sleepQuality,
        tags: entry.tags,
        triggers: entry.triggers
      });
      
      // Extract the therapeutic response from the analysis
      const response: TherapeuticResponse = {
        type: determineResponseType(analysis),
        message: analysis.therapeuticResponse.message,
        techniques: analysis.therapeuticResponse.techniques,
        exercises: analysis.therapeuticResponse.exercises,
        followUp: analysis.therapeuticResponse.followUp
      };
      
      // Cache the result
      setAnalysisCache(prev => ({
        ...prev,
        [cacheKey]: response,
        [`sentiment_${entryId}`]: {
          score: analysis.sentiment.score,
          magnitude: 0.8,
          emotions: analysis.sentiment.emotions,
          confidence: 0.9
        },
        [`distortions_${entryId}`]: analysis.cognitiveDistortions,
        [`risk_${entryId}`]: {
          ...analysis.riskAssessment,
          requiresIntervention: analysis.riskAssessment.level === 'crisis' || analysis.riskAssessment.level === 'high'
        }
      }));
      
      console.log("Generated therapeutic response:", response);
      return response;
    } catch (error) {
      console.error('Error generating therapeutic response:', error);
      
      // Fallback to local generation if the OpenAI call fails
      return fallbackGenerateResponse(entryId);
    } finally {
      setIsAnalyzing(false);
      setLastAnalysis(new Date());
    }
  };

  // Determine the response type based on the analysis
  const determineResponseType = (analysis: any): 'validation' | 'cbt' | 'mindfulness' | 'coping' | 'goal-setting' => {
    if (analysis.cognitiveDistortions && analysis.cognitiveDistortions.length > 0) {
      return 'cbt';
    }
    
    if (analysis.sentiment.score < -0.3) {
      return 'validation';
    }
    
    if (analysis.sentiment.emotions.anxiety > 0.5) {
      return 'coping';
    }
    
    if (analysis.sentiment.score > 0.3) {
      return 'goal-setting';
    }
    
    return 'mindfulness';
  };

  // Fallback function if the OpenAI call fails
  const fallbackGenerateResponse = async (entryId: string): Promise<TherapeuticResponse> => {
    const entry = entries.find(e => e.id === entryId);
    if (!entry) {
      return {
        type: 'validation',
        message: 'Thank you for sharing your thoughts.',
        techniques: [],
        exercises: [],
        followUp: []
      };
    }
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const sentiment = await analyzeSentiment(entry.content);
    const distortions = await detectCognitiveDistortions(entry.content);
    
    let response: TherapeuticResponse;
    
    if (distortions.length > 0) {
      response = {
        type: 'cbt',
        message: `I notice some thought patterns that might be affecting how you feel. ${distortions[0].suggestion}`,
        techniques: ['Thought challenging', 'Evidence examination', 'Balanced thinking'],
        exercises: [
          'Write down the evidence for and against this thought',
          'Ask yourself: "What would I tell a friend in this situation?"',
          'Consider alternative explanations for the situation'
        ],
        followUp: [
          'How did it feel to challenge these thoughts?',
          'What evidence did you find that surprised you?'
        ]
      };
    } else if (sentiment.score < -0.3) {
      response = {
        type: 'validation',
        message: 'It sounds like you\'re going through a difficult time. Your feelings are valid, and it\'s okay to feel this way.',
        techniques: ['Self-compassion', 'Emotional validation', 'Mindful acceptance'],
        exercises: [
          'Practice the self-compassion break',
          'Write yourself a kind letter',
          'Try the 5-4-3-2-1 grounding technique'
        ],
        followUp: [
          'What small act of kindness can you show yourself today?',
          'How can you honor these feelings while also caring for yourself?'
        ]
      };
    } else if (entry.mood >= 7) {
      response = {
        type: 'validation',
        message: 'I\'m glad to hear you\'re feeling positive! This is wonderful progress.',
        techniques: ['Gratitude practice', 'Positive reinforcement', 'Strength identification'],
        exercises: [
          'Identify what contributed to this positive mood',
          'Write down three things you did well today',
          'Plan how to maintain this positive momentum'
        ],
        followUp: [
          'What strategies helped you feel this way?',
          'How can you build on this positive experience?'
        ]
      };
    } else {
      response = {
        type: 'mindfulness',
        message: 'Thank you for taking the time to reflect and write. Mindful awareness is a powerful tool for growth.',
        techniques: ['Mindful breathing', 'Present moment awareness', 'Non-judgmental observation'],
        exercises: [
          'Take 5 deep breaths and notice how you feel',
          'Practice a brief body scan',
          'Set an intention for the rest of your day'
        ],
        followUp: [
          'What did you notice during your mindfulness practice?',
          'How might mindfulness help you in challenging moments?'
        ]
      };
    }
    
    return response;
  };

  const getInsights = (): AIInsight[] => {
    return insights;
  };

  const generateProgressSummary = async (period: 'week' | 'month'): Promise<ProgressSummary> => {
    try {
      console.log(`Generating ${period} progress summary`);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const now = new Date();
      const periodStart = new Date();
      periodStart.setDate(now.getDate() - (period === 'week' ? 7 : 30));
      
      const periodEntries = entries.filter(entry => entry.createdAt >= periodStart);
      
      if (periodEntries.length === 0) {
        return {
          period,
          moodTrend: 'stable',
          keyInsights: ['Not enough data for this period'],
          achievements: [],
          areasForFocus: [],
          goalProgress: [],
          recommendations: ['Continue journaling regularly to track your progress']
        };
      }
      
      const avgMood = periodEntries.reduce((sum, entry) => sum + entry.mood, 0) / periodEntries.length;
      const previousPeriodStart = new Date(periodStart);
      previousPeriodStart.setDate(periodStart.getDate() - (period === 'week' ? 7 : 30));
      
      const previousEntries = entries.filter(entry => 
        entry.createdAt >= previousPeriodStart && entry.createdAt < periodStart
      );
      
      const previousAvgMood = previousEntries.length > 0 
        ? previousEntries.reduce((sum, entry) => sum + entry.mood, 0) / previousEntries.length 
        : avgMood;
      
      const moodTrend: 'improving' | 'stable' | 'declining' = 
        avgMood > previousAvgMood + 0.5 ? 'improving' :
        avgMood < previousAvgMood - 0.5 ? 'declining' : 'stable';
      
      const keyInsights = [
        `You wrote ${periodEntries.length} journal entries this ${period}`,
        `Your average mood was ${avgMood.toFixed(1)}/10`,
        `Most common tags: ${Array.from(new Set(periodEntries.flatMap(e => e.tags))).slice(0, 3).join(', ')}`
      ];
      
      const achievements = [];
      if (periodEntries.length >= (period === 'week' ? 5 : 20)) {
        achievements.push('Consistent journaling practice');
      }
      if (avgMood >= 7) {
        achievements.push('Maintained positive mood');
      }
      if (periodEntries.some(e => e.gratitude.length > 0)) {
        achievements.push('Practiced gratitude regularly');
      }
      
      const areasForFocus = [];
      if (avgMood < 5) {
        areasForFocus.push('Mood support and coping strategies');
      }
      if (periodEntries.some(e => e.anxiety >= 7)) {
        areasForFocus.push('Anxiety management techniques');
      }
      if (periodEntries.some(e => e.sleepQuality <= 4)) {
        areasForFocus.push('Sleep hygiene and quality');
      }
      
      const result = {
        period,
        moodTrend,
        keyInsights,
        achievements,
        areasForFocus,
        goalProgress: user?.profile?.mentalHealthGoals?.map(goal => ({
          goal,
          progress: Math.random() * 40 + 30 // Simulated progress
        })) || [],
        recommendations: [
          'Continue your regular journaling practice',
          'Consider exploring mindfulness techniques',
          'Celebrate your progress and small wins'
        ]
      };
      
      console.log("Generated progress summary:", result);
      return result;
    } catch (error) {
      console.error("Error generating progress summary:", error);
      throw error;
    }
  };

  const getMoodPatterns = () => {
    try {
      const last30Days = entries
        .filter(entry => {
          const daysDiff = (Date.now() - entry.createdAt.getTime()) / (1000 * 60 * 60 * 24);
          return daysDiff <= 30;
        })
        .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime())
        .map(entry => ({
          date: entry.createdAt.toISOString().split('T')[0],
          mood: entry.mood,
          anxiety: entry.anxiety,
          energy: entry.energy,
          sleep: entry.sleepQuality
        }));
      
      return last30Days;
    } catch (error) {
      console.error("Error getting mood patterns:", error);
      return [];
    }
  };

  const getBehavioralPatterns = () => {
    try {
      const tagFrequency: Record<string, number> = {};
      const triggerFrequency: Record<string, number> = {};
      
      entries.forEach(entry => {
        entry.tags.forEach(tag => {
          tagFrequency[tag] = (tagFrequency[tag] || 0) + 1;
        });
        entry.triggers.forEach(trigger => {
          triggerFrequency[trigger] = (triggerFrequency[trigger] || 0) + 1;
        });
      });
      
      // Calculate time of day distribution
      const timeOfDayData = [
        { time: 'morning', count: 0 },
        { time: 'afternoon', count: 0 },
        { time: 'evening', count: 0 },
        { time: 'night', count: 0 }
      ];
      
      entries.forEach(entry => {
        const hour = entry.createdAt.getHours();
        if (hour >= 5 && hour < 12) {
          timeOfDayData[0].count++;
        } else if (hour >= 12 && hour < 17) {
          timeOfDayData[1].count++;
        } else if (hour >= 17 && hour < 22) {
          timeOfDayData[2].count++;
        } else {
          timeOfDayData[3].count++;
        }
      });
      
      // Calculate average word count by mood level
      const lowMoodEntries = entries.filter(e => e.mood <= 4);
      const mediumMoodEntries = entries.filter(e => e.mood > 4 && e.mood <= 7);
      const highMoodEntries = entries.filter(e => e.mood > 7);
      
      const avgLowMoodWords = lowMoodEntries.length > 0 
        ? lowMoodEntries.reduce((sum, e) => sum + e.wordCount, 0) / lowMoodEntries.length 
        : 0;
      
      const avgMediumMoodWords = mediumMoodEntries.length > 0 
        ? mediumMoodEntries.reduce((sum, e) => sum + e.wordCount, 0) / mediumMoodEntries.length 
        : 0;
      
      const avgHighMoodWords = highMoodEntries.length > 0 
        ? highMoodEntries.reduce((sum, e) => sum + e.wordCount, 0) / highMoodEntries.length 
        : 0;
      
      return {
        topTags: Object.entries(tagFrequency)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 10)
          .map(([tag, count]) => ({ tag, count })),
        topTriggers: Object.entries(triggerFrequency)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 10)
          .map(([trigger, count]) => ({ trigger, count })),
        journalingPatterns: {
          mostActiveTime: timeOfDayData.sort((a, b) => b.count - a.count)[0].time,
          averageEntryLength: entries.length > 0 
            ? entries.reduce((sum, e) => sum + e.wordCount, 0) / entries.length 
            : 0,
          consistencyScore: Math.min(100, entries.length * 5) // Simple metric based on entry count
        },
        timeOfDayData,
        avgWordCountByMood: {
          low: avgLowMoodWords,
          medium: avgMediumMoodWords,
          high: avgHighMoodWords
        }
      };
    } catch (error) {
      console.error("Error getting behavioral patterns:", error);
      return { topTags: [], topTriggers: [] };
    }
  };

  const getGoalProgress = () => {
    if (!user?.profile?.mentalHealthGoals) return [];
    
    return user.profile.mentalHealthGoals.map(goal => ({
      goal,
      progress: Math.random() * 60 + 20, // Simulated progress
      trend: Math.random() > 0.5 ? 'up' : 'down',
      lastUpdate: new Date()
    }));
  };

  // Generate insights periodically
  useEffect(() => {
    if (entries.length > 0) {
      const generateInsights = async () => {
        console.log("Generating AI insights based on journal entries");
        
        const newInsights: AIInsight[] = [];
        
        // Mood pattern insight
        const recentMoods = entries.slice(0, 7).map(e => e.mood);
        const avgRecentMood = recentMoods.reduce((a, b) => a + b, 0) / recentMoods.length;
        
        if (avgRecentMood >= 7) {
          newInsights.push({
            id: 'mood-positive',
            type: 'celebration',
            title: 'Positive Mood Trend',
            description: 'Your mood has been consistently positive this week. Great work!',
            actionItems: ['Continue current coping strategies', 'Share what\'s working with others'],
            priority: 'low',
            createdAt: new Date()
          });
        } else if (avgRecentMood <= 4) {
          newInsights.push({
            id: 'mood-concern',
            type: 'concern',
            title: 'Mood Support Needed',
            description: 'Your mood has been lower than usual. Consider additional support.',
            actionItems: ['Reach out to support network', 'Practice self-care', 'Consider professional help'],
            priority: 'high',
            createdAt: new Date()
          });
        }
        
        // Journaling consistency
        const last7Days = entries.filter(entry => {
          const daysDiff = (Date.now() - entry.createdAt.getTime()) / (1000 * 60 * 60 * 24);
          return daysDiff <= 7;
        });
        
        if (last7Days.length >= 5) {
          newInsights.push({
            id: 'consistency-celebration',
            type: 'celebration',
            title: 'Consistent Journaling',
            description: 'You\'ve been journaling regularly this week. This consistency is building a strong foundation for self-awareness.',
            actionItems: ['Keep up the great work', 'Notice patterns in your entries'],
            priority: 'low',
            createdAt: new Date()
          });
        }
        
        // Anxiety pattern
        const highAnxietyEntries = entries.filter(e => e.anxiety >= 7);
        if (highAnxietyEntries.length >= 3) {
          newInsights.push({
            id: 'anxiety-pattern',
            type: 'pattern',
            title: 'Anxiety Patterns Detected',
            description: 'Several of your recent entries show high anxiety levels. This might be worth exploring.',
            actionItems: [
              'Practice daily relaxation techniques',
              'Identify specific anxiety triggers',
              'Consider anxiety-focused coping strategies'
            ],
            priority: 'medium',
            createdAt: new Date()
          });
        }
        
        // Sleep quality insight
        const poorSleepEntries = entries.filter(e => e.sleepQuality <= 4);
        if (poorSleepEntries.length >= 3) {
          newInsights.push({
            id: 'sleep-concern',
            type: 'concern',
            title: 'Sleep Quality Alert',
            description: 'Your sleep quality has been consistently low. Poor sleep can impact mood and overall wellbeing.',
            actionItems: [
              'Establish a regular sleep schedule',
              'Create a relaxing bedtime routine',
              'Limit screen time before bed',
              'Consider sleep hygiene improvements'
            ],
            priority: 'medium',
            createdAt: new Date()
          });
        }
        
        setInsights(newInsights);
      };
      
      generateInsights();
    }
  }, [entries, user?.profile?.mentalHealthGoals]);

  const value = {
    analyzeSentiment,
    detectCognitiveDistortions,
    identifyTriggers,
    assessRisk,
    generateTherapeuticResponse,
    getInsights,
    generateProgressSummary,
    getMoodPatterns,
    getBehavioralPatterns,
    getGoalProgress,
    isAnalyzing,
    setIsAnalyzing,
    lastAnalysis
  };

  return <AIAnalysisContext.Provider value={value}>{children}</AIAnalysisContext.Provider>;
};